import * as React from "react";

import { revealWindow } from "./layout";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import { WHEEL_NOTCH_PX, ZOOM_PER_PX } from "./useTimelineGestures";
import {
  clampWindow,
  initialWindow,
  panWindow,
  sameWindow,
  spanOf,
  type TimeWindow,
  type WindowUpdate,
  zoomWindow,
} from "./window";

/** `pan` moves the window by this fraction of its span (Shift+← / →). */
export const PAN_FRACTION = 0.1;
/** `zoomIn` / `zoomOut` scale the span by the same factor as one wheel notch with Ctrl/⌘ held. */
export const KEY_ZOOM_FACTOR = Math.exp(WHEEL_NOTCH_PX * ZOOM_PER_PX);

export interface TimelineStateOptions<T> {
  /** Where the window may go: the loaded history's extent. */
  extent: TimeWindow;
  /** The window to open on; read once, on mount. Defaults to the last 30 days of the extent. */
  initialTimeWindow?: TimeWindow;
  /** Called after every window change (not on the opening one). */
  onTimeWindowChange?: (window: TimeWindow) => void;
  /** The selected item. A change after mount moves the window to show it. */
  selectedItem: TimelineTrackItem<T> | null;
}

/**
 * The window and every action that moves it. Each result is clamped to the
 * extent. Immediate — no animation.
 */
export interface TimelineActions<T> {
  timeWindow: TimeWindow;
  /** Replace the window, or compute it from the current one. */
  setTimeWindow: (update: WindowUpdate) => void;
  /** Narrow the span around its center by `KEY_ZOOM_FACTOR`. */
  zoomIn: () => void;
  /** Widen the span around its center by `KEY_ZOOM_FACTOR`. */
  zoomOut: () => void;
  /** Slide the window earlier (`-1`) or later (`1`) by `PAN_FRACTION` of its span. */
  pan: (sign: -1 | 1) => void;
  /** Show the whole history: the window becomes the extent. */
  fitAll: () => void;
  /** Move the window just enough to show `item`; nothing when it is already comfortably in view. */
  reveal: (item: TimelineTrackItem<T>) => void;
}

/**
 * The window and the actions that change it, for one loaded history: the
 * extent is fixed for the hook's lifetime, so a view of new data mounts it
 * again rather than adjusting it. Every window action goes through one
 * `setTimeWindow` whose updater sees the current window, so callers never
 * hold a copy of it: no "latest value" refs, and no stale closures in
 * listeners attached once.
 *
 * Window changes are transitions: when a new window needs a chunk that is
 * not loaded yet, the tracks suspend inside the transition and React keeps
 * the previous clips on screen instead of the fallback.
 */
export function useTimelineState<T>({
  extent,
  initialTimeWindow,
  onTimeWindowChange,
  selectedItem,
}: TimelineStateOptions<T>): TimelineActions<T> {
  const [opening] = React.useState(() =>
    initialWindow(extent, initialTimeWindow),
  );
  const [timeWindow, setWindow] = React.useState(opening);

  // The page hears about a change once per change, from an effect: a setState
  // updater must stay pure (StrictMode runs it twice). The opening window is
  // not a change. Keyed on the window, not the callback, so an inline
  // callback does not re-announce the same window.
  React.useEffect(() => {
    if (timeWindow !== opening) {
      onTimeWindowChange?.(timeWindow);
    }
  }, [timeWindow]); // oxlint-disable-line react-hooks/exhaustive-deps -- fire per window change, not per callback identity

  const setTimeWindow = React.useCallback(
    (update: WindowUpdate) => {
      React.startTransition(() => {
        setWindow((previous) => {
          const requested =
            typeof update === "function" ? update(previous) : update;
          if (requested === null) {
            return previous;
          }
          const next = clampWindow(requested, extent);
          return sameWindow(next, previous) ? previous : next;
        });
      });
    },
    [extent],
  );

  const zoomIn = React.useCallback(() => {
    setTimeWindow((window) => zoomWindow(window, 1 / KEY_ZOOM_FACTOR));
  }, [setTimeWindow]);

  const zoomOut = React.useCallback(() => {
    setTimeWindow((window) => zoomWindow(window, KEY_ZOOM_FACTOR));
  }, [setTimeWindow]);

  const pan = React.useCallback(
    (sign: -1 | 1) => {
      setTimeWindow((window) =>
        panWindow(window, sign * PAN_FRACTION * spanOf(window)),
      );
    },
    [setTimeWindow],
  );

  const fitAll = React.useCallback(() => {
    setTimeWindow(extent);
  }, [extent, setTimeWindow]);

  const reveal = React.useCallback(
    (item: TimelineTrackItem<T>) => {
      setTimeWindow((window) => revealWindow(item, window, extent));
    },
    [extent, setTimeWindow],
  );

  // Auto-reveal: a selection made after mount is brought into view, once per
  // selected id. The mount-time selection is left alone — the initial window
  // wins over it. The page hands over the item itself: the streamer only
  // holds the chunk it has loaded, and a selection from a tray or a deep link
  // may lie outside it.
  const [revealedId, setRevealedId] = React.useState(selectedItem?.id ?? null);
  React.useEffect(() => {
    if (selectedItem === null || selectedItem.id === revealedId) {
      return;
    }
    setRevealedId(selectedItem.id);
    reveal(selectedItem);
  }, [selectedItem, revealedId, reveal]);

  return {
    timeWindow,
    setTimeWindow,
    zoomIn,
    zoomOut,
    pan,
    fitAll,
    reveal,
  };
}
